import { Component, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DocumentListComponent } from '../document-list/document-list.component';
import { GenericColDef, GridContext } from '../../models/grid-context';
import { Subscription } from 'rxjs';
import { MainEmailThreadDocument } from '../../models/dr-document';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { Endpoint, MessageType, UpdateStatusType } from '../../models/enums';
import { View } from '../../models/view';
import { SavedSearch } from '../../models/saved-searches';
import { Comparison } from '../../models/comparison';
import { DrRequestInfo } from '../../models/dr-request-info';
import { PagerComponent } from '../pager/pager.component';
import { isString } from '../../models/utils';

@Component({
	standalone: false,
	selector: 'short-message-threads',
	templateUrl: './short-message-threads.component.html',
	styleUrls: ['./short-message-threads.component.scss']
})

export class ShortMessageThreadsComponent implements OnInit, OnDestroy {
	@ViewChild('shortMessageGrid', { static: true }) shortMessageGrid: DocumentListComponent;
	@HostBinding('class') classes = 'maximized-component';

	private entityChangedEvent: Subscription;
	columnDefs: Array<GenericColDef>;

	gridContext = {
		metadata: new GridContext({
			sortAscending: true,
			sortEnabled: false
		})
	};

	gridPagerCustomCallback = (startIndex: any) => {
		if (startIndex !== undefined) {
			this.shortMessageGrid.genericGrid.isBusy = true;
			this.loadData(startIndex);
		}
	}

	responseCallback = (response: any) => {
		if (response) {

			if (response.emailThreadsSummary) {
				response.totalCount = response.emailThreadsSummary.threadCount;
			}

			if (response.threads) {
				const threads = (<Array<any>>response.threads).map(t => new MainEmailThreadDocument(t));
				response.threads = threads;
			}
		}
	}

	constructor(private commonService: CommonService, private restService: RestService) {
		this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
			const shortMessageTab = this.commonService.selectedNavigationNode.navigationInfo.tabs.find(tab => tab.name === 'Short Message Threads');
			if (data === UpdateStatusType.WORKING_SET && shortMessageTab && this.commonService.selectedNavigationNode.navigationInfo.selectedTab.name === 'Short Message Threads') {
				this.loadData();
			}
		});
	}

	ngOnInit(): void {
		this.shortMessageGrid.isShortMessageThreads = true;
		this.shortMessageGrid.selectedTreeItemChanged(this.commonService.selectedNavigationNode);
		this.loadData();
	}

	loadData(startIndex?): void {
		if (this.shortMessageGrid?.genericGrid) {
			this.shortMessageGrid.genericGrid.suppressRefresh = true;
		}
		const entity = this.commonService.selectedNavigationNode.data;
		let entityView = this.commonService.selectedNavigationNode.data;
		if (entity.view instanceof View || entity instanceof SavedSearch) {
			entityView = entity.view;
		} else if (entity.workingSetView instanceof View) {
			entityView = entity.workingSetView;
		}

		const requestInfo = {
			workingSetHandle: entityView instanceof Comparison ? entityView.docListHandle : entityView.handle,
			count: 100,
			startIndex: startIndex ? startIndex : 0,
			threadType: 'smf'
		};

		this.restService.post(Endpoint.workingSetViewManager_listEmailThreads, requestInfo).then(response => {
			if (response?.handle) {
				this.listThreads(response, startIndex);
			} else {
				this.shortMessageGrid.genericGrid.isBusy = false;
			}
		}, () => {
			if (this.shortMessageGrid.genericGrid) {
				this.shortMessageGrid.genericGrid.isBusy = false;
				this.shortMessageGrid.genericGrid.suppressRefresh = false;
			}
		});
	}

	noFilesProjectData(): boolean {
		try {
			return this.commonService?.projectData?.documentCount === 0;
		} catch (err) {
			return false;
		}
	}

	listThreads(response, startIndex): void {
		this.shortMessageGrid.requestInfo = new DrRequestInfo({
			endpoint: Endpoint.workingSetViewManager_listEmailThreadsResult,
			responseName: 'threads',
			data: { handle: response.handle }
		});
		if (this.shortMessageGrid.genericGrid) {
			this.shortMessageGrid.genericGrid.pager.startIndex = startIndex ? startIndex : 0;
			this.shortMessageGrid.genericGrid.pager.endIndex = Math.min(this.shortMessageGrid.genericGrid.pager.startIndex + PagerComponent.itemsOnPage, this.shortMessageGrid.genericGrid.pager.totalItems);
			this.shortMessageGrid.genericGrid.pager.currentPage = (Math.ceil(this.shortMessageGrid.genericGrid.pager.startIndex / 100) + 1);
			this.shortMessageGrid.genericGrid.suppressRefresh = false;
		}
		this.shortMessageGrid.loadData();
	}

	refreshThreads(): void {
		if (this.commonService.projectData?.handle) {

			this.shortMessageGrid.genericGrid.isBusy = true;
			this.shortMessageGrid.genericGrid.isBusyMessage = 'Updating threads...';

			this.restService.post(Endpoint.workingSetViewManager_unthreadEmails, { workingSetViewHandle: this.commonService.projectData.handle, taskDescription: 'Unthreading Emails' }).then(response => {
				if (isString(response)) {
					this.shortMessageGrid.genericGrid.isBusyMessage = 'Unthreading emails...' + response + '%...';
				}
				this.restService.post(Endpoint.workingSetViewManager_threadEmails, { workingSetViewHandle: this.commonService.projectData.handle, taskDescription: 'Threading Emails' }).then(response => {
					if (isString(response)) {
						this.shortMessageGrid.genericGrid.isBusyMessage = 'Threading emails...' + response + '%...';
					} else if (response?.handle) {
						this.loadData();
					}
				}, () => {
					this.shortMessageGrid.genericGrid.isBusy = false;
				});
			}, () => {
				this.shortMessageGrid.genericGrid.isBusy = false;
				this.ngOnDestroy();
			});

		}
	}

	ngOnDestroy(): void {
		if (this.entityChangedEvent) {
			this.entityChangedEvent.unsubscribe();
		}
	}

}
